<script lang="ts">
  // Publishing many families at once, from either of the two places they live: a folder of .rfa files,
  // or the open project itself.
  //
  // Separate from PublishDialog on purpose. Publishing one family is a considered act — you look at
  // the 3D preview, you name it, you write a description. Publishing forty is a different job: nobody
  // reads forty previews, and the metadata worth setting is the metadata they share. So this screen
  // asks for the shared half once and takes everything else from each family.
  //
  // The loop is sequential, and that is not laziness. Each extract opens a family document in Revit,
  // which is single-threaded by definition; firing forty at once would queue on the same thread and
  // lose the per-family progress that makes a long run bearable to watch.

  import { transport } from "@/transport";
  import { auth } from "@/stores/auth.svelte";
  import { categoryLabel, LOD_LEVELS } from "./categories";
  import { PICKER_TIMEOUT_MS } from "@/transport/types";
  import type {
    FamilyPackage,
    ProjectFamily,
    ProjectFamilyList,
    PublishRequest,
    PublishResult,
  } from "./types";
  import { t, locale } from "@/i18n/index.svelte";
  import type { SupportedLocale } from "@/i18n/index.svelte";
  import {
    AppButton,
    AppDialog,
    AppInput,
    AppSelect,
    AppState,
  } from "@/components/ui";

  let {
    onclose,
    onpublished,
  }: { onclose?: () => void; onpublished?: () => void } = $props();

  type Source = "choose" | "files" | "project" | "systemTypes";
  type RowState = "pending" | "working" | "done" | "failed" | "skipped";

  interface Row {
    key: string;
    label: string;
    /** Set for the file source; the extract command takes a path. */
    path?: string;
    /** Set for both project sources; the extract command takes an ElementId. */
    id?: number;
    /** Set only for a system type — and it is what decides which extract command the row needs. */
    systemTypeClass?: string | null;
    category?: string;
    typeCount?: number;
    instanceCount?: number;
    selected: boolean;
    state: RowState;
    message?: string;
    slug?: string;
  }

  let source = $state<Source>("choose");
  let loading = $state(false);
  let error = $state("");
  let rows = $state<Row[]>([]);
  let running = $state(false);
  let finished = $state(false);

  // Narrows the list to one category at a time — tick every wall, switch the filter, tick every
  // window — instead of hunting through hundreds of rows in one undifferentiated list. Empty means
  // "all categories". Selection itself is untouched by the filter: it only hides rows, so a
  // category ticked earlier stays ticked when you filter back to something else.
  let categoryFilter = $state("");

  // The shared half. Everything else — name, category, OmniClass, units, year — is read from each
  // family, which is the whole reason a batch is possible at all.
  let shared = $state({
    subcategory: "",
    tags: "",
    visibility: "public" as "public" | "org",
    // Required, like the single-family publish form — a batch of forty different families is still
    // forty families with a real LOD each, but asking per-row would defeat "shared half asked once".
    // If a batch genuinely mixes LODs, publish it in two passes rather than one with the wrong badge
    // on half the rows.
    lod: "" as string,
  });

  // Why the Publish button below is disabled — running's own disablement is self-explained by the
  // label already switching to "Publishing…", so it stays silent.
  const publishDisabledReason = $derived.by(() => {
    if (running) return null;
    if (!selected.length) return t("batch.selectRequired");
    if (!shared.lod) return t("publish.lodRequired");
    return null;
  });

  const selected = $derived(rows.filter((r) => r.selected));
  const published = $derived(rows.filter((r) => r.state === "done").length);
  const failed = $derived(rows.filter((r) => r.state === "failed").length);

  // One entry per distinct category actually present, with a count — built from the rows
  // themselves rather than a fixed list, so it only ever offers categories this project has.
  const categoryOptions = $derived.by(() => {
    const counts = new Map<string, number>();
    for (const row of rows) {
      if (row.category)
        counts.set(row.category, (counts.get(row.category) ?? 0) + 1);
    }
    return [...counts.entries()]
      .map(([value, count]) => ({
        value,
        count,
        label: categoryLabel(value, locale.value as SupportedLocale),
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  });

  const visibleRows = $derived(
    categoryFilter ? rows.filter((r) => r.category === categoryFilter) : rows,
  );

  // TEMPORARY: no "private" — see PublishDialog and PublishService. A family only its author can
  // see is a file in a folder, which the plugin browses without uploading anything.
  const visibilityOptions = $derived.by(() => {
    const options: { value: "public" | "org"; label: string }[] = [
      { value: "public", label: t("publish.visibilityPublic") },
    ];
    if (auth.state.organizationId) {
      options.push({
        value: "org",
        label: auth.state.organizationName || t("publish.visibilityOrg"),
      });
    }
    return options;
  });

  async function chooseFiles() {
    error = "";
    try {
      // No default timeout: this waits on the native file dialog, i.e. on the user, not on Revit.
      const res = await transport.invoke<{ paths: string[] }>(
        "PickFiles",
        { multiple: true },
        { timeoutMs: PICKER_TIMEOUT_MS },
      );
      const paths = res.paths ?? [];
      if (!paths.length) return; // cancelled

      rows = paths.map((path) => ({
        key: path,
        label: fileName(path),
        path,
        selected: true,
        state: "pending" as RowState,
      }));
      categoryFilter = "";
      source = "files";
    } catch (e) {
      error = messageOf(e);
    }
  }

  /**
   * Both project sources, which differ only in which command answers.
   *
   * Loadable families and system types are two Revit concepts with nothing in common under the hood —
   * one is a file, the other cannot be a file — but to the person deciding what their office should
   * publish they are the same decision, so they get the same screen.
   */
  async function loadProject(kind: "families" | "systemTypes") {
    error = "";
    loading = true;
    try {
      const list = await transport.invoke<ProjectFamilyList>(
        kind === "families" ? "ListProjectFamilies" : "ListProjectSystemTypes",
      );
      if (!list.ok) {
        error = list.error ?? t("batch.projectFailed");
        return;
      }

      rows = list.families.map((f: ProjectFamily) => ({
        key: String(f.id),
        label: f.name,
        id: f.id,
        systemTypeClass: f.systemTypeClass,
        category: f.category,
        typeCount: f.typeCount,
        instanceCount: f.instanceCount,
        // Nothing is ticked to begin with. A project holds hundreds of families and types, most of
        // them Autodesk's own defaults, and a screen that arrives with all of them selected invites
        // publishing the entire out-of-the-box content library by accident.
        selected: false,
        state: "pending" as RowState,
      }));
      categoryFilter = "";
      source = kind === "families" ? "project" : "systemTypes";
    } catch (e) {
      error = messageOf(e);
    } finally {
      loading = false;
    }
  }

  // Scoped to what the category filter currently shows, so "select all" means "all walls" while
  // filtered to walls, not the whole project.
  function toggleAll(value: boolean) {
    for (const row of visibleRows)
      if (row.state === "pending") row.selected = value;
  }

  /**
   * Publishes the ticked rows one at a time.
   *
   * A failure does not stop the run. In a batch of forty the interesting outcome is "thirty-eight
   * worked, these two did not and here is why" — aborting on the first would make the whole feature
   * hostage to one corrupt file.
   */
  async function publishAll() {
    if (running || !selected.length || !shared.lod) return;
    running = true;
    finished = false;
    error = "";

    const tags = shared.tags
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);
    const subcategory = shared.subcategory.trim() || null;

    for (const row of rows) {
      if (!row.selected || row.state === "done") continue;

      row.state = "working";
      row.message = "";

      try {
        // No preview: nothing on this screen renders one, and forty meshes over the WebView bridge is
        // the oversized-message problem forty times over. The mesh is still built and still reaches
        // the catalog — it just does not come back through the UI.
        const command = row.path
          ? "ExtractFamilyPackage"
          : row.systemTypeClass
            ? "ExtractSystemType"
            : "ExtractProjectFamily";

        console.log(`[batch] ${command} start`, row.key);
        const pkg = await transport.invoke<FamilyPackage>(
          command,
          row.path
            ? { localPath: row.path, includePreview: false }
            : { id: row.id, includePreview: false },
        );
        console.log(`[batch] ${command} done`, row.key, pkg.ok);

        if (!pkg.ok) {
          row.state = "failed";
          row.message = pkg.error ?? t("publish.extractFailed");
          continue;
        }

        const request: PublishRequest = {
          name: pkg.suggestedName,
          revitCategory: pkg.revitCategory,
          subcategory,
          sourceFileName: pkg.sourceFileName,
          // Read from the family. A batch never asks for a classification, because the only honest
          // answer for forty different families is forty different codes.
          omniClassCode: pkg.omniClassCode,
          omniClassTitle: pkg.omniClassTitle,
          description: "",
          tags,
          authoringLanguage: pkg.suggestedLanguage ?? null,
          unitSystem: pkg.unitSystem,
          visibility: shared.visibility,
          lod: Number(shared.lod),
          // Straight from the package rather than from the row: the host decided what it produced,
          // and a system type published as "loadable" would be undeliverable.
          kind: pkg.kind ?? "loadable",
          systemTypeClass: pkg.systemTypeClass ?? null,
          localPath: pkg.localPath,
          revitYear: pkg.revitYear,
          sha256: pkg.sha256,
          sizeBytes: pkg.sizeBytes,
          mesh: null,
          thumbnailPngBase64: null,
          parameters: pkg.parameters,
          types: pkg.types,
        };

        console.log("[batch] UploadFamily start", row.key);
        const result = await transport.invoke<PublishResult>(
          "UploadFamily",
          request,
        );
        console.log("[batch] UploadFamily done", row.key, result.ok);
        if (result.ok) {
          row.state = "done";
          row.slug = result.slug ?? undefined;
          row.message =
            result.status === "pending"
              ? t("publish.pending")
              : t("publish.done");
        } else {
          row.state = "failed";
          row.message = result.error ?? t("batch.publishFailed");
        }
      } catch (e) {
        row.state = "failed";
        row.message = messageOf(e);
      }
    }

    running = false;
    finished = true;
    if (published > 0) onpublished?.();
  }

  function fileName(path: string): string {
    return path.split(/[\\/]/).pop() ?? path;
  }

  function messageOf(e: unknown): string {
    return e instanceof Error ? e.message : String(e);
  }

  function stateClass(state: RowState): string {
    if (state === "done") return "text-success";
    if (state === "failed") return "text-error";
    if (state === "working") return "text-accent";
    return "text-ink-muted";
  }
</script>

<AppDialog
  size="lg"
  closeLabel={t("publish.close")}
  closeOnOverlay={false}
  closeOnEscape={false}
  onclose={() => onclose?.()}
>
  {#snippet header()}
    <h2 class="text-lg font-semibold">{t("batch.title")}</h2>
    <p class="text-sm text-ink-muted">{t("batch.subtitle")}</p>
  {/snippet}

  {#if error}
    <p class="text-sm text-error">{error}</p>
  {/if}

  <!-- Where the families are coming from. The three options disappear once one is chosen and is
       loading — left on screen next to the spinner, they read as still choosable. -->
  {#if source === "choose"}
    <div class="flex flex-col gap-3">
      {#if !loading}
        <button
          class="text-left rounded-[--brand-radius-surface] border border-line p-4 hover:border-accent transition-colors"
          onclick={chooseFiles}
        >
          <p class="font-medium">{t("batch.fromFiles")}</p>
          <p class="text-sm text-ink-muted">{t("batch.fromFilesHint")}</p>
        </button>

        <button
          class="text-left rounded-[--brand-radius-surface] border border-line p-4 hover:border-accent transition-colors"
          onclick={() => loadProject("families")}
        >
          <p class="font-medium">{t("batch.fromProject")}</p>
          <p class="text-sm text-ink-muted">{t("batch.fromProjectHint")}</p>
        </button>

        <!-- The one an office actually needs. Wall build-ups, floor assemblies and pipe types are
             what a firm's standards *are*, and they are the part that cannot be put in a folder. -->
        <button
          class="text-left rounded-[--brand-radius-surface] border border-line p-4 hover:border-accent transition-colors"
          onclick={() => loadProject("systemTypes")}
        >
          <p class="font-medium">{t("batch.fromSystemTypes")}</p>
          <p class="text-sm text-ink-muted">{t("batch.fromSystemTypesHint")}</p>
        </button>
      {:else}
        <AppState kind="loading">{t("common.loading")}</AppState>
      {/if}
    </div>
  {:else if !rows.length}
    <AppState kind="empty">{t("batch.nothingFound")}</AppState>
  {:else}
    <!-- The shared half, asked once. -->
    <div class="grid grid-cols-1 sm:grid-cols-4 gap-3">
      <label class="flex flex-col gap-1">
        <span class="text-xs font-medium text-ink-muted"
          >{t("publish.visibility")}</span
        >
        <AppSelect bind:value={shared.visibility} disabled={running}>
          {#each visibilityOptions as o (o.value)}
            <option value={o.value}>{o.label}</option>
          {/each}
        </AppSelect>
      </label>

      <label class="flex flex-col gap-1">
        <span class="text-xs font-medium text-ink-muted"
          >{t("publish.lod")}</span
        >
        <AppSelect bind:value={shared.lod} disabled={running}>
          <option value="" disabled>—</option>
          {#each LOD_LEVELS as l (l)}
            <option value={String(l)}>LOD {l}</option>
          {/each}
        </AppSelect>
      </label>

      <label class="flex flex-col gap-1">
        <span class="text-xs font-medium text-ink-muted"
          >{t("publish.subcategory")}</span
        >
        <AppInput
          bind:value={shared.subcategory}
          type="text"
          disabled={running}
          placeholder={t("publish.subcategoryHint")}
        />
      </label>

      <label class="flex flex-col gap-1">
        <span class="text-xs font-medium text-ink-muted"
          >{t("publish.tags")}</span
        >
        <AppInput
          bind:value={shared.tags}
          type="text"
          disabled={running}
          placeholder={t("publish.tagsHint")}
        />
      </label>
    </div>

    <div class="flex items-center gap-3 text-sm">
      {#if categoryOptions.length > 1}
        <!-- Only worth showing when there is more than one category to narrow down — a single-
             category batch (or a folder of loose files, which carries no category at all) has
             nothing for it to filter. -->
        <AppSelect bind:value={categoryFilter} size="xs" disabled={running}>
          <option value="">{t("batch.allCategories")}</option>
          {#each categoryOptions as c (c.value)}
            <option value={c.value}>{c.label} ({c.count})</option>
          {/each}
        </AppSelect>
      {/if}
      <AppButton
        variant="ghost"
        size="xs"
        disabled={running}
        onclick={() => toggleAll(true)}
      >
        {t("batch.selectAll")}
      </AppButton>
      <AppButton
        variant="ghost"
        size="xs"
        disabled={running}
        onclick={() => toggleAll(false)}
      >
        {t("batch.selectNone")}
      </AppButton>
      <span class="ml-auto text-xs text-ink-muted tabular-nums">
        {t("batch.selectedCount", {
          selected: selected.length,
          total: rows.length,
        })}
      </span>
    </div>

    <ul
      class="border border-line rounded-control divide-y divide-line max-h-80 overflow-auto"
    >
      {#each visibleRows as row (row.key)}
        <li class="flex items-center gap-3 px-3 py-2">
          <input
            bind:checked={row.selected}
            type="checkbox"
            disabled={running || row.state === "done"}
            class="shrink-0"
          />
          <span class="flex-1 min-w-0">
            <span class="block truncate text-sm">{row.label}</span>
            {#if row.category}
              <span class="block text-xs text-ink-muted truncate">
                {categoryLabel(row.category, locale.value as SupportedLocale)}
                · {t("batch.types", { count: row.typeCount ?? 0 })}
                · {t("batch.instances", { count: row.instanceCount ?? 0 })}
              </span>
            {/if}
          </span>
          <span
            class="shrink-0 text-xs max-w-56 truncate {stateClass(row.state)}"
          >
            {row.message || t(`batch.state.${row.state}`)}
          </span>
        </li>
      {/each}
    </ul>

    {#if finished}
      <p class="text-sm">
        {t("batch.summary", { published, failed })}
      </p>
    {/if}

    <div class="flex items-center gap-2">
      <span class="contents" title={publishDisabledReason ?? undefined}>
        <AppButton
          variant="primary"
          size="md"
          disabled={running || !selected.length || !shared.lod}
          onclick={publishAll}
        >
          {running
            ? t("batch.publishing")
            : t("batch.publish", { count: selected.length })}
        </AppButton>
      </span>
      <AppButton
        variant="ghost"
        size="md"
        disabled={running}
        onclick={() => onclose?.()}
      >
        {t("publish.close")}
      </AppButton>
    </div>
  {/if}
</AppDialog>
